import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import { listAlerts } from "../lib/api-modules/alerts.js";
import {
  backtestSignalRule,
  createSignalRule,
  deleteSignalRule,
  describeRule,
  listSignalRules,
  updateSignalRule,
} from "../lib/api-modules/signal-rules.js";
import {
  ALL_HOLDINGS_TICKER,
  type Alert,
  type CreateSignalRuleInput,
  type IndicatorKey,
  type MembershipRole,
  type ScalarIndicatorKey,
  type SignalComparator,
  type SignalDirection,
  type SignalMode,
  type SignalRule,
  type SignalRuleConfig,
} from "../lib/api-modules/types.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Tab, Tabs } from "../components/design-system/index.js";
import { Toast, useToast } from "../components/toast.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";
import { normalizeTicker } from "../lib/util/merrill-ticker-map.js";

/**
 * /alerts — household-scoped CRUD for signal rules + the History/
 * Backtest tabs added in Phase 15.5. The daily 6:30pm-ET signal-scanner
 * Lambda evaluates every armed rule against fresh EOD bars; firings
 * land in the existing alerts table + send a SES email to the
 * household owner (subject + body rewritten in 15.5 to be direction-
 * prefixed and carry per-rule interpretation copy).
 *
 * Read access for every member; write access (create/edit/delete) for
 * owner/admin/member/contributor. Viewer + auditor see the list with
 * no write controls and an explainer banner.
 *
 * Three rule kinds:
 *   - Threshold: indicator <comparator> value (e.g. rsi_14 < 30)
 *   - Cross: fast crosses {up,down} slow (e.g. sma_50 crosses up sma_200)
 *   - Bollinger band: close {above,below} {upper,lower} band
 *
 * Three modes (Phase 15.5):
 *   - direction: buy / sell / info — surfaces in the email subject
 *   - mode: single (one ticker) / all_holdings (every ticker the
 *     household holds, dynamically resolved at scan time, excluding
 *     UTMA/529 accounts)
 *   - cooldown_hours: 24 default — stops daily refire while a position
 *     remains in trigger-state.
 */

type ActiveTab = "rules" | "history" | "backtest";

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

const SCALAR_INDICATORS: readonly ScalarIndicatorKey[] = [
  "sma_20",
  "sma_50",
  "sma_200",
  "ema_20",
  "ema_50",
  "ema_200",
  "rsi_14",
  "atr_14",
  "vwap",
];

const COMPARATORS: readonly SignalComparator[] = ["<", ">", "<=", ">="];

interface StarterTemplate {
  readonly id: string;
  readonly label: string;
  readonly defaultName: string;
  readonly direction: SignalDirection;
  readonly draft: RuleDraft;
}

const STARTER_TEMPLATES: readonly StarterTemplate[] = [
  {
    id: "rsi_oversold",
    label: "RSI < 30 (oversold)",
    defaultName: "RSI oversold reentry",
    direction: "buy",
    draft: {
      kind: "threshold",
      threshold: { indicator: "rsi_14", comparator: "<", value: "30" },
      cross: { fast: "sma_20", slow: "sma_50", direction: "up" },
      bbBand: { band: "upper", direction: "above" },
    },
  },
  {
    id: "rsi_overbought",
    label: "RSI > 70 (overbought)",
    defaultName: "RSI overbought trim",
    direction: "sell",
    draft: {
      kind: "threshold",
      threshold: { indicator: "rsi_14", comparator: ">", value: "70" },
      cross: { fast: "sma_20", slow: "sma_50", direction: "up" },
      bbBand: { band: "upper", direction: "above" },
    },
  },
  {
    id: "golden_cross",
    label: "Golden cross (50/200)",
    defaultName: "Golden cross",
    direction: "buy",
    draft: {
      kind: "cross",
      threshold: { indicator: "rsi_14", comparator: "<", value: "30" },
      cross: { fast: "sma_50", slow: "sma_200", direction: "up" },
      bbBand: { band: "upper", direction: "above" },
    },
  },
  {
    id: "death_cross",
    label: "Death cross (50/200)",
    defaultName: "Death cross",
    direction: "sell",
    draft: {
      kind: "cross",
      threshold: { indicator: "rsi_14", comparator: "<", value: "30" },
      cross: { fast: "sma_50", slow: "sma_200", direction: "down" },
      bbBand: { band: "upper", direction: "above" },
    },
  },
  {
    id: "bb_upper_break",
    label: "Bollinger upper breakout",
    defaultName: "Bollinger upper breakout",
    direction: "sell",
    draft: {
      kind: "bb_band",
      threshold: { indicator: "rsi_14", comparator: "<", value: "30" },
      cross: { fast: "sma_20", slow: "sma_50", direction: "up" },
      bbBand: { band: "upper", direction: "above" },
    },
  },
];

export function AlertsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<ActiveTab>("rules");
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Alerts</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          Signal rules — evaluated daily after market close. Threshold lines on
          the chart show how close each rule is to firing.
        </p>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as ActiveTab)}
        ariaLabel="Alerts sections"
      >
        <Tab value="rules" label="Rules">
          <RulesTab canWrite={canWrite} />
        </Tab>
        <Tab value="history" label="History">
          <HistoryTab />
        </Tab>
        <Tab value="backtest" label="Backtest">
          <BacktestTab />
        </Tab>
      </Tabs>

      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.alerts} />
      </div>
    </div>
  );
}

// === Rules tab ==========================================================

function RulesTab({ canWrite }: { canWrite: boolean }) {
  return (
    <>
      {canWrite && <CreateRuleForm />}
      <RulesList canWrite={canWrite} />
    </>
  );
}

function RulesList({ canWrite }: { canWrite: boolean }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["signal-rules"],
    queryFn: listSignalRules,
  });

  return (
    <section aria-labelledby="rules-heading" className="mt-6">
      <h2 id="rules-heading" className="mb-3 text-lg font-medium">
        Your rules
      </h2>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load rules:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && data.signal_rules.length === 0 && (
        <p className="text-sm text-zinc-500">No rules yet.</p>
      )}
      {data && data.signal_rules.length > 0 && (
        <ul className="space-y-3">
          {data.signal_rules.map((r) => (
            <RuleCard key={r.signal_rule_id} rule={r} canWrite={canWrite} />
          ))}
        </ul>
      )}
    </section>
  );
}

function DirectionBadge({ direction }: { direction: SignalDirection }) {
  if (direction === "buy") {
    return (
      <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
        Buy
      </span>
    );
  }
  if (direction === "sell") {
    return (
      <span className="rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
        Sell
      </span>
    );
  }
  return (
    <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
      Info
    </span>
  );
}

function RuleCard({
  rule,
  canWrite,
}: {
  rule: SignalRule;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <li className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
        <EditRuleForm rule={rule} onDone={() => setEditing(false)} />
      </li>
    );
  }
  const armed = rule.is_armed ?? true;
  const direction = rule.direction ?? "info";
  const isAllHoldings = rule.ticker === ALL_HOLDINGS_TICKER;
  return (
    <li className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <DirectionBadge direction={direction} />
            <h3 className="font-medium">{rule.name}</h3>
            <span
              className={
                armed
                  ? "rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
              }
            >
              {armed ? "armed" : "disarmed"}
            </span>
            {isAllHoldings ? (
              <span className="rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                All holdings
              </span>
            ) : (
              <Link
                to={`/chart/${encodeURIComponent(rule.ticker)}`}
                className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
              >
                {rule.ticker}
              </Link>
            )}
          </div>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            {describeRule(rule.rule)}
          </p>
          {rule.last_fired_at && (
            <p className="mt-1 text-xs text-zinc-500">
              Last fired{" "}
              <time dateTime={rule.last_fired_at}>
                {formatRelativeTime(rule.last_fired_at)}
              </time>
              {typeof rule.last_fired_value === "number" &&
                ` @ ${rule.last_fired_value.toFixed(4)}`}
            </p>
          )}
        </div>
        {canWrite && (
          <div className="flex shrink-0 flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Edit
            </button>
            <DeleteButton rule={rule} />
          </div>
        )}
      </div>
    </li>
  );
}

function DeleteButton({ rule }: { rule: SignalRule }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => deleteSignalRule(rule.signal_rule_id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["signal-rules"] });
    },
  });
  return (
    <ConfirmButton
      label="Delete"
      pendingLabel="Deleting…"
      onConfirm={() => mutation.mutate()}
      isPending={mutation.isPending}
    />
  );
}

// === Create + Edit forms ================================================

function CreateRuleForm() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [ticker, setTicker] = useState("");
  const [draft, setDraft] = useState<RuleDraft>(initialDraft);
  const [direction, setDirection] = useState<SignalDirection>("info");
  const [mode, setMode] = useState<SignalMode>("single");
  const [cooldownHours, setCooldownHours] = useState<string>("24");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: () => {
      const rule = draftToRule(draft);
      if (!rule) throw new Error("Rule fields incomplete");
      const cooldown = Number(cooldownHours);
      if (!Number.isFinite(cooldown) || cooldown < 1 || cooldown > 168) {
        throw new Error("Cooldown must be between 1 and 168 hours");
      }
      const input: CreateSignalRuleInput = {
        name,
        rule,
        direction,
        mode,
        cooldown_hours: cooldown,
      };
      if (mode === "single") {
        input.ticker = normalizeTicker(ticker);
      }
      return createSignalRule(input);
    },
    onSuccess: async (createdRule) => {
      setName("");
      setTicker("");
      setDraft(initialDraft);
      setDirection("info");
      setMode("single");
      setCooldownHours("24");
      setShowAdvanced(false);
      toast.show(`Created "${createdRule.name}"`);
      await queryClient.invalidateQueries({ queryKey: ["signal-rules"] });
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't create. Try again.",
      );
    },
  });

  function applyStarter(template: StarterTemplate) {
    setName(template.defaultName);
    setDraft(template.draft);
    setDirection(template.direction);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  const submitDisabled =
    mutation.isPending ||
    name.length === 0 ||
    (mode === "single" && ticker.trim().length === 0) ||
    !draftToRule(draft);

  return (
    <section aria-labelledby="create-heading" className="mt-2">
      <h2 id="create-heading" className="mb-3 text-lg font-medium">
        New rule
      </h2>
      <div className="mb-3 flex flex-wrap gap-2">
        <span className="self-center text-xs text-zinc-500">
          Start from a template:
        </span>
        {STARTER_TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => applyStarter(t)}
            className="rounded-full border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
          >
            {t.label}
          </button>
        ))}
      </div>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor="r-name" className="mb-1 block text-sm font-medium">
              Name
            </label>
            <input
              id="r-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
              disabled={mutation.isPending}
              placeholder="AAPL oversold"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Apply to</label>
            <div className="flex gap-3 text-sm">
              <label className="inline-flex items-center gap-1">
                <input
                  type="radio"
                  name="mode"
                  value="single"
                  checked={mode === "single"}
                  onChange={() => setMode("single")}
                  disabled={mutation.isPending}
                />
                One ticker
              </label>
              <label className="inline-flex items-center gap-1">
                <input
                  type="radio"
                  name="mode"
                  value="all_holdings"
                  checked={mode === "all_holdings"}
                  onChange={() => setMode("all_holdings")}
                  disabled={mutation.isPending}
                />
                All my holdings
              </label>
            </div>
          </div>
        </div>
        {mode === "single" && (
          <div>
            <label htmlFor="r-ticker" className="mb-1 block text-sm font-medium">
              Ticker
            </label>
            <input
              id="r-ticker"
              type="text"
              required
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              className="w-full max-w-xs rounded-md border border-zinc-300 px-3 py-2 text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
              disabled={mutation.isPending}
              placeholder="AAPL"
            />
          </div>
        )}
        {mode === "all_holdings" && (
          <p className="rounded-md bg-blue-50 p-2 text-xs text-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
            This rule will evaluate against every ticker in your portfolio at
            scan time, automatically excluding UTMA + 529 accounts. Adding a
            new buy will extend the rule with no extra setup.
          </p>
        )}
        <DirectionPicker
          value={direction}
          onChange={setDirection}
          disabled={mutation.isPending}
        />
        <RuleDraftEditor draft={draft} onChange={setDraft} disabled={mutation.isPending} />
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((s) => !s)}
            className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
          >
            {showAdvanced ? "Hide" : "Show"} advanced
          </button>
        </div>
        {showAdvanced && (
          <div>
            <label htmlFor="r-cooldown" className="mb-1 block text-sm font-medium">
              Cooldown (hours)
            </label>
            <input
              id="r-cooldown"
              type="number"
              min={1}
              max={168}
              step={1}
              value={cooldownHours}
              onChange={(e) => setCooldownHours(e.target.value)}
              className="w-32 rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
              disabled={mutation.isPending}
            />
            <p className="mt-1 text-xs text-zinc-500">
              Don't fire the same rule on the same ticker more than once in
              this many hours. Default is 24h.
            </p>
          </div>
        )}
        <div>
          <button
            type="submit"
            disabled={submitDisabled}
            className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? "Creating…" : "Create rule"}
          </button>
        </div>
      </form>
      {inlineError && (
        <div className="mt-3">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
      <Toast message={toast.message} />
    </section>
  );
}

function EditRuleForm({
  rule,
  onDone,
}: {
  rule: SignalRule;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(rule.name);
  const [draft, setDraft] = useState<RuleDraft>(ruleToDraft(rule.rule));
  const [isArmed, setIsArmed] = useState(rule.is_armed ?? true);
  const [direction, setDirection] = useState<SignalDirection>(
    rule.direction ?? "info",
  );
  const [cooldownHours, setCooldownHours] = useState<string>(
    String(rule.cooldown_hours ?? 24),
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const next = draftToRule(draft);
      if (!next) throw new Error("Rule fields incomplete");
      const cooldown = Number(cooldownHours);
      if (!Number.isFinite(cooldown) || cooldown < 1 || cooldown > 168) {
        throw new Error("Cooldown must be between 1 and 168 hours");
      }
      return updateSignalRule(rule.signal_rule_id, {
        name,
        rule: next,
        is_armed: isArmed,
        direction,
        cooldown_hours: cooldown,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["signal-rules"] });
      onDone();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't update. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  const submitDisabled =
    mutation.isPending || name.length === 0 || !draftToRule(draft);

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <div>
        <label
          htmlFor={`r-edit-name-${rule.signal_rule_id}`}
          className="mb-1 block text-sm font-medium"
        >
          Name
        </label>
        <input
          id={`r-edit-name-${rule.signal_rule_id}`}
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={mutation.isPending}
        />
      </div>
      <DirectionPicker
        value={direction}
        onChange={setDirection}
        disabled={mutation.isPending}
      />
      <RuleDraftEditor draft={draft} onChange={setDraft} disabled={mutation.isPending} />
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isArmed}
          onChange={(e) => setIsArmed(e.target.checked)}
          disabled={mutation.isPending}
        />
        Armed (scanner evaluates daily)
      </label>
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced((s) => !s)}
          className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          {showAdvanced ? "Hide" : "Show"} advanced
        </button>
      </div>
      {showAdvanced && (
        <div>
          <label
            htmlFor={`r-edit-cooldown-${rule.signal_rule_id}`}
            className="mb-1 block text-sm font-medium"
          >
            Cooldown (hours)
          </label>
          <input
            id={`r-edit-cooldown-${rule.signal_rule_id}`}
            type="number"
            min={1}
            max={168}
            step={1}
            value={cooldownHours}
            onChange={(e) => setCooldownHours(e.target.value)}
            className="w-32 rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            disabled={mutation.isPending}
          />
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitDisabled}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

function DirectionPicker({
  value,
  onChange,
  disabled,
}: {
  value: SignalDirection;
  onChange: (v: SignalDirection) => void;
  disabled: boolean;
}) {
  return (
    <fieldset>
      <legend className="mb-1 text-sm font-medium">Direction</legend>
      <div className="flex flex-wrap gap-3 text-sm">
        {(["buy", "sell", "info"] as const).map((d) => (
          <label key={d} className="inline-flex items-center gap-1">
            <input
              type="radio"
              name={`direction-${value}`}
              value={d}
              checked={value === d}
              onChange={() => onChange(d)}
              disabled={disabled}
            />
            {d === "buy" ? "Buy" : d === "sell" ? "Sell" : "Info"}
          </label>
        ))}
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        Drives email subject + chart marker color. Use Info for rules that
        aren't about entering or exiting a position.
      </p>
    </fieldset>
  );
}

// === History tab ========================================================

function HistoryTab() {
  const [tickerFilter, setTickerFilter] = useState<string>("");
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["alerts", "signal_fired", "history", 90],
    queryFn: () => listAlerts({ type: "signal_fired", days: 90 }),
  });
  const filtered = useMemo(() => {
    const all = data?.alerts ?? [];
    const t = tickerFilter.trim().toUpperCase();
    if (!t) return all;
    return all.filter(
      (a) => (a.fired_ticker ?? "").toUpperCase() === t,
    );
  }, [data, tickerFilter]);

  return (
    <section className="mt-2">
      <div className="mb-4 flex items-end gap-3">
        <div>
          <label htmlFor="hist-ticker" className="mb-1 block text-xs font-medium">
            Ticker
          </label>
          <input
            id="hist-ticker"
            type="text"
            value={tickerFilter}
            onChange={(e) => setTickerFilter(e.target.value)}
            className="w-32 rounded-md border border-zinc-300 px-3 py-1.5 text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            placeholder="All"
          />
        </div>
        <p className="ml-auto text-xs text-zinc-500">Last 90 days</p>
      </div>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          {error instanceof Error ? error.message : "Couldn't load history"}
        </ErrorMessage>
      )}
      {data && filtered.length === 0 && (
        <p className="text-sm text-zinc-500">
          No fired alerts {tickerFilter ? `for ${tickerFilter}` : ""} in the
          last 90 days.
        </p>
      )}
      {filtered.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-600 dark:bg-zinc-900 dark:text-zinc-400">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Ticker</th>
                <th className="px-3 py-2 text-left">Direction</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Body</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <HistoryRow key={a.alert_id} alert={a} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function HistoryRow({ alert }: { alert: Alert }) {
  const ticker = alert.fired_ticker ?? "—";
  const direction = alert.direction ?? "info";
  return (
    <tr className="border-t border-zinc-200 dark:border-zinc-800">
      <td className="whitespace-nowrap px-3 py-2 text-xs text-zinc-600 dark:text-zinc-400">
        {alert.timestamp.slice(0, 10)}
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        {ticker !== "—" ? (
          <Link
            to={`/chart/${encodeURIComponent(ticker)}`}
            className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
          >
            {ticker}
          </Link>
        ) : (
          <span className="text-zinc-500">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <DirectionBadge direction={direction} />
      </td>
      <td className="px-3 py-2">{alert.title}</td>
      <td className="px-3 py-2 text-xs text-zinc-600 dark:text-zinc-400">
        {alert.body}
      </td>
    </tr>
  );
}

// === Backtest tab =======================================================

function BacktestTab() {
  const { data: rulesResp } = useQuery({
    queryKey: ["signal-rules"],
    queryFn: listSignalRules,
  });
  const armedRules = (rulesResp?.signal_rules ?? []).filter(
    (r) => r.is_archived !== true && r.ticker !== ALL_HOLDINGS_TICKER,
  );
  const [selectedRuleId, setSelectedRuleId] = useState<string>("");
  const [tickerOverride, setTickerOverride] = useState<string>("");
  const [days, setDays] = useState<string>("365");
  const [error, setError] = useState<string | null>(null);

  const selectedRule = armedRules.find(
    (r) => r.signal_rule_id === selectedRuleId,
  );
  const effectiveTicker = (
    tickerOverride.trim() || (selectedRule?.ticker ?? "")
  ).toUpperCase();

  const mutation = useMutation({
    mutationFn: () => {
      if (!selectedRule) throw new Error("Pick a rule first");
      const daysNum = Number(days);
      if (!Number.isFinite(daysNum) || daysNum < 30 || daysNum > 730) {
        throw new Error("Days must be between 30 and 730");
      }
      const ticker = effectiveTicker;
      if (!ticker) throw new Error("Ticker is required");
      return backtestSignalRule({
        rule: selectedRule.rule,
        ticker,
        days: daysNum,
      });
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Backtest failed",
      );
    },
    onSuccess: () => setError(null),
  });

  return (
    <section className="mt-2 space-y-4">
      <p className="text-sm text-zinc-600 dark:text-zinc-400">
        See how often a rule would have fired against historical EOD prices.
        This is a fire-count check, not a P&L backtest.
      </p>
      {armedRules.length === 0 && (
        <p className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          You don't have any single-ticker rules to backtest yet. Create a rule
          on the Rules tab first.
        </p>
      )}
      {armedRules.length > 0 && (
        <div className="grid grid-cols-1 gap-3 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800 md:grid-cols-3">
          <div>
            <label htmlFor="bt-rule" className="mb-1 block text-sm font-medium">
              Rule
            </label>
            <select
              id="bt-rule"
              value={selectedRuleId}
              onChange={(e) => setSelectedRuleId(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            >
              <option value="">— pick one —</option>
              {armedRules.map((r) => (
                <option key={r.signal_rule_id} value={r.signal_rule_id}>
                  {r.name} ({r.ticker})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="bt-ticker" className="mb-1 block text-sm font-medium">
              Ticker (override)
            </label>
            <input
              id="bt-ticker"
              type="text"
              value={tickerOverride}
              onChange={(e) => setTickerOverride(e.target.value)}
              placeholder={selectedRule?.ticker ?? ""}
              className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            />
          </div>
          <div>
            <label htmlFor="bt-days" className="mb-1 block text-sm font-medium">
              Lookback (days)
            </label>
            <input
              id="bt-days"
              type="number"
              min={30}
              max={730}
              step={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            />
          </div>
          <div className="md:col-span-3">
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={!selectedRule || !effectiveTicker || mutation.isPending}
              className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending ? "Running…" : "Run backtest"}
            </button>
          </div>
        </div>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {mutation.data && (
        <div className="rounded-md border border-zinc-200 p-4 text-sm dark:border-zinc-800">
          <p className="font-medium">
            Would have fired{" "}
            <strong>{mutation.data.fire_count}</strong> times on{" "}
            {mutation.data.ticker} in the last {mutation.data.days} days
            <span className="ml-1 text-zinc-500">
              ({mutation.data.total_bars} bars evaluated)
            </span>
          </p>
          {mutation.data.warning && (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              {mutation.data.warning}
            </p>
          )}
          {mutation.data.fire_dates.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
                Fire dates
              </summary>
              <p className="mt-2 font-mono text-zinc-600 dark:text-zinc-400">
                {mutation.data.fire_dates.join(", ")}
              </p>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

// === Rule draft editor ==================================================
//
// The editor renders one of three sub-forms based on the user's `kind`
// selection. We keep the draft in a single discriminated state so kind
// switches don't lose the user's other field values immediately —
// switching back from `cross` → `threshold` returns the threshold values
// the user previously typed.

interface RuleDraft {
  kind: "threshold" | "cross" | "bb_band";
  threshold: { indicator: ScalarIndicatorKey; comparator: SignalComparator; value: string };
  cross: { fast: ScalarIndicatorKey; slow: ScalarIndicatorKey; direction: "up" | "down" };
  bbBand: { band: "upper" | "lower"; direction: "above" | "below" };
}

const initialDraft: RuleDraft = {
  kind: "threshold",
  threshold: { indicator: "rsi_14", comparator: "<", value: "30" },
  cross: { fast: "sma_20", slow: "sma_50", direction: "up" },
  bbBand: { band: "upper", direction: "above" },
};

function ruleToDraft(rule: SignalRuleConfig): RuleDraft {
  const draft: RuleDraft = { ...initialDraft };
  if (rule.kind === "threshold") {
    draft.kind = "threshold";
    draft.threshold = {
      indicator: rule.indicator,
      comparator: rule.comparator,
      value: String(rule.value),
    };
  } else if (rule.kind === "cross") {
    draft.kind = "cross";
    draft.cross = {
      fast: rule.fast,
      slow: rule.slow,
      direction: rule.direction,
    };
  } else {
    draft.kind = "bb_band";
    draft.bbBand = { band: rule.band, direction: rule.direction };
  }
  return draft;
}

function draftToRule(draft: RuleDraft): SignalRuleConfig | null {
  if (draft.kind === "threshold") {
    const value = Number(draft.threshold.value);
    if (!Number.isFinite(value)) return null;
    return {
      kind: "threshold",
      indicator: draft.threshold.indicator,
      comparator: draft.threshold.comparator,
      value,
    };
  }
  if (draft.kind === "cross") {
    if (draft.cross.fast === draft.cross.slow) return null;
    return {
      kind: "cross",
      fast: draft.cross.fast,
      slow: draft.cross.slow,
      direction: draft.cross.direction,
    };
  }
  return {
    kind: "bb_band",
    band: draft.bbBand.band,
    direction: draft.bbBand.direction,
  };
}

function RuleDraftEditor({
  draft,
  onChange,
  disabled,
}: {
  draft: RuleDraft;
  onChange: (next: RuleDraft) => void;
  disabled: boolean;
}) {
  return (
    <div className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
      <div className="mb-3 flex flex-wrap gap-2">
        <KindButton
          kind="threshold"
          label="Threshold"
          active={draft.kind === "threshold"}
          onClick={() => onChange({ ...draft, kind: "threshold" })}
          disabled={disabled}
        />
        <KindButton
          kind="cross"
          label="MA cross"
          active={draft.kind === "cross"}
          onClick={() => onChange({ ...draft, kind: "cross" })}
          disabled={disabled}
        />
        <KindButton
          kind="bb_band"
          label="Bollinger band"
          active={draft.kind === "bb_band"}
          onClick={() => onChange({ ...draft, kind: "bb_band" })}
          disabled={disabled}
        />
      </div>

      {draft.kind === "threshold" && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <Select
            label="Indicator"
            value={draft.threshold.indicator}
            options={SCALAR_INDICATORS}
            onChange={(v) =>
              onChange({
                ...draft,
                threshold: { ...draft.threshold, indicator: v },
              })
            }
            disabled={disabled}
          />
          <Select
            label="Comparator"
            value={draft.threshold.comparator}
            options={COMPARATORS}
            onChange={(v) =>
              onChange({
                ...draft,
                threshold: { ...draft.threshold, comparator: v },
              })
            }
            disabled={disabled}
          />
          <NumberInput
            label="Value"
            value={draft.threshold.value}
            onChange={(v) =>
              onChange({ ...draft, threshold: { ...draft.threshold, value: v } })
            }
            disabled={disabled}
          />
        </div>
      )}

      {draft.kind === "cross" && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <Select
            label="Fast"
            value={draft.cross.fast}
            options={SCALAR_INDICATORS}
            onChange={(v) =>
              onChange({
                ...draft,
                cross: { ...draft.cross, fast: v },
              })
            }
            disabled={disabled}
          />
          <Select
            label="Slow"
            value={draft.cross.slow}
            options={SCALAR_INDICATORS}
            onChange={(v) =>
              onChange({
                ...draft,
                cross: { ...draft.cross, slow: v },
              })
            }
            disabled={disabled}
          />
          <Select
            label="Direction"
            value={draft.cross.direction}
            options={["up", "down"] as const}
            onChange={(v) =>
              onChange({
                ...draft,
                cross: { ...draft.cross, direction: v },
              })
            }
            disabled={disabled}
          />
        </div>
      )}

      {draft.kind === "bb_band" && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Select
            label="Band"
            value={draft.bbBand.band}
            options={["upper", "lower"] as const}
            onChange={(v) =>
              onChange({
                ...draft,
                bbBand: { ...draft.bbBand, band: v },
              })
            }
            disabled={disabled}
          />
          <Select
            label="Close is"
            value={draft.bbBand.direction}
            options={["above", "below"] as const}
            onChange={(v) =>
              onChange({
                ...draft,
                bbBand: { ...draft.bbBand, direction: v },
              })
            }
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

function KindButton({
  kind: _kind,
  label,
  active,
  onClick,
  disabled,
}: {
  kind: string;
  label: string;
  active: boolean;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        active
          ? "rounded-md bg-brand-500 px-3 py-1 text-xs font-medium text-white"
          : "rounded-md border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
      }
    >
      {label}
    </button>
  );
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
  disabled: boolean;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
        {label}
      </span>
      <input
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={disabled}
      />
    </label>
  );
}

// Quiet the unused-IndicatorKey import; the type is re-exported from
// types.ts for downstream consumers (e.g. tests) and we'd rather keep
// the surface clean here.
export type { IndicatorKey };

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
